import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Activity,
  ChevronRight,
  History,
  LayoutGrid,
  Lock,
  LogOut,
  ShieldCheck,
  Terminal,
  TrendingUp,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { AppLayout } from "@/components/layout/AppLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAgentsStore } from "@/lib/agents-store";
import {
  useActiveWorkspace,
  useAuthEmail,
  useAuthFullName,
  useAuthLogout,
  useSwitchWorkspace,
  useUserRole,
  useUserWorkspaces,
} from "@/lib/auth-store";
import { useComplianceStore } from "@/lib/compliance-store";
import { useTranslation } from "@/lib/i18n";

const PERFORMANCE_DATA = [
  { name: "00:00", requests: 400, errors: 24 },
  { name: "04:00", requests: 300, errors: 18 },
  { name: "08:00", requests: 900, errors: 45 },
  { name: "12:00", requests: 1200, errors: 60 },
  { name: "16:00", requests: 1800, errors: 55 },
  { name: "20:00", requests: 800, errors: 20 },
  { name: "23:59", requests: 500, errors: 15 },
];

const MOCK_EVENTS = [
  { id: 1, type: "Security", source: "Auth Service", msg: "API Key Rotated", time: "2m ago" },
  { id: 2, type: "Agent", source: "Deployment", msg: "New Agent Deployed", time: "15m ago" },
  { id: 3, type: "Compliance", source: "Scan Hub", msg: "Compliance Check Passed", time: "1h ago" },
  { id: 4, type: "System", source: "Worker", msg: "Backup Sync Completed", time: "3h ago" },
];

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthLogout();
  const fullName = useAuthFullName();
  const email = useAuthEmail();
  const userRole = useUserRole();
  const activeWorkspace = useActiveWorkspace();
  const workspaces = useUserWorkspaces();
  const switchWorkspace = useSwitchWorkspace();
  const agents = useAgentsStore((s) => s.agents);
  const riskScore = useComplianceStore((s) => s.riskScore);

  const handleLogout = () => {
    logout();
    toast.info("Logged out successfully");
    navigate("/login");
  };

  const stats = useMemo(
    () => [
      { label: t("active_sessions"), value: "1,284", icon: Activity, color: "text-chart-3" },
      { label: t("system_status"), value: "99.9%", icon: ShieldCheck, color: "text-primary" },
      { label: t("user_overview"), value: `${agents.length} Agents`, icon: Users, color: "text-chart-4" },
      { label: t("risk_score"), value: `${100 - riskScore}%`, icon: Lock, color: "text-chart-2" },
    ],
    [t, agents.length, riskScore]
  );

  return (
    <AppLayout container>
      <div className="space-y-8">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">
                {t("welcome_dashboard")}, {fullName || email?.split("@")[0] || "User"}
              </h1>
              <Badge variant="secondary" className="border border-primary/20 bg-primary/10 text-primary">
                {userRole}
              </Badge>
            </div>
            <p className="mt-1 text-muted-foreground">
              Workspace: <span className="font-semibold text-foreground">{activeWorkspace.name}</span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 rounded-full border-white/50 bg-card/70 shadow-soft">
                  <LayoutGrid className="h-4 w-4" />
                  {t("workspace_switcher")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {workspaces.map((ws) => (
                  <DropdownMenuItem key={ws.id} onClick={() => switchWorkspace(ws.id)}>
                    {ws.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              onClick={handleLogout}
              className="gap-2 rounded-full border-white/50 bg-card/70 shadow-soft"
            >
              <LogOut className="h-4 w-4" />
              {t("logout")}
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="border-white/40 bg-card/72 backdrop-blur-xl shadow-soft">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">{stat.label}</CardTitle>
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    <span className="font-medium text-primary">+2.1%</span> vs last week
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2 border-white/40 bg-card/72 p-6 backdrop-blur-xl shadow-soft">
            <h3 className="mb-6 flex items-center gap-2 text-lg font-semibold">
              <TrendingUp className="h-5 w-5 text-primary" /> {t("system_performance")}
            </h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={PERFORMANCE_DATA}>
                  <defs>
                    <linearGradient id="colorReq" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.32} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted))" />
                  <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      borderRadius: "12px",
                      border: "1px solid hsl(var(--border))",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="requests"
                    stroke="hsl(var(--primary))"
                    fill="url(#colorReq)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="border-white/40 bg-card/72 p-6 backdrop-blur-xl shadow-soft">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
              <History className="h-5 w-5 text-chart-4" /> {t("live_events")}
            </h3>
            <div className="space-y-4">
              {MOCK_EVENTS.map((ev) => (
                <div key={ev.id} className="flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-muted/30">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    {ev.type === "Security" ? (
                      <Lock className="h-4 w-4 text-chart-2" />
                    ) : (
                      <Terminal className="h-4 w-4 text-chart-3" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold">{ev.msg}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {ev.source} • {ev.time}
                    </p>
                  </div>
                </div>
              ))}

              <Button variant="ghost" className="w-full text-xs" onClick={() => navigate("/audit")}>
                {t("view_all")} <ChevronRight className="ml-2 h-3 w-3" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
